"use client";

/**
 * Message input bar with send button.
 *
 * Extracted from `compass-step1.jsx:298-304` (the `.cs-comp`, `.cs-send` classes).
 * Sticky-footer chat composer with a text input and a send button.
 */

import { ArrowR } from "../lib/icons";

export interface ChatComposerProps {
  /** Current input value (controlled). */
  value?: string;
  /** Placeholder text when the input is empty. */
  placeholder?: string;
  /** Whether the input has user content (drives visual styling). */
  hasContent?: boolean;
  /** Called when the send button is clicked. */
  onSend?: () => void;
  /** Called when the input value changes. */
  onChange?: (value: string) => void;
}

/**
 * Chat composer bar — text input + navy send button.
 *
 * `.cs-comp` → flex row, bg-white, border, rounded-field, shadow-xs
 * `.cs-send` → 38×38 navy circle, white arrow
 */
export function ChatComposer({
  value = "",
  placeholder = "Type a message…",
  hasContent,
  onSend,
  onChange,
}: ChatComposerProps) {
  const filled = hasContent ?? value.trim().length > 0;

  return (
    <div
      className={[
        "flex items-center gap-2",
        "bg-(--bg-white) border border-(--border-default)",
        "rounded-full",
        "py-[9px] pl-[18px] pr-[9px]",
        "shadow-(--shadow-xs)",
      ].join(" ")}
    >
      <input
        type="text"
        className={[
          "flex-1 min-w-0",
          "bg-transparent border-0 outline-none",
          "font-[family-name:var(--font-body)] text-[15px]",
          filled ? "text-(--fg-primary)" : "text-(--fg-muted)",
          "placeholder:text-(--fg-muted)",
        ].join(" ")}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange?.(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && filled) onSend?.();
        }}
      />
      <button
        type="button"
        disabled={!filled}
        className={[
          "flex-shrink-0",
          "w-[38px] h-[38px]",
          "rounded-full",
          "flex items-center justify-center",
          "border-0 cursor-pointer",
          "transition-colors duration-150",
          filled
            ? "bg-(--btn-primary-bg) text-(--btn-primary-fg)"
            : "bg-(--border-default) text-(--fg-subtle) cursor-default",
        ].join(" ")}
        aria-label="Send message"
        onClick={() => filled && onSend?.()}
      >
        <ArrowR size={18} />
      </button>
    </div>
  );
}
